/**
 * Notification destinations.
 *
 * A notification is only useful if clicking it lands the user on the thing it
 * is about. This module is the single place that decides where a notification
 * navigates, under three hard rules:
 *
 *  1. Only canonical application routes. Destinations are built from an
 *     allow-list of route patterns — never from a URL supplied in the payload,
 *     and never from a request Host header.
 *  2. Role-safe. A patient is never routed into a doctor/admin surface, and
 *     vice versa. The role comes from the authenticated session, not the
 *     notification row.
 *  3. Unknown or non-actionable types return null, so the UI can present an
 *     honest non-actionable item instead of a broken link.
 *
 * Entity ids must be positive integers, so nothing else in the payload can be
 * smuggled into a route.
 */

export type NotificationRole = 'patient' | 'doctor' | 'nurse' | 'admin' | string | null | undefined;

export interface NotificationLike {
  type?: string | null;
  data?: unknown;
}

/** Parse the JSON `data` column whether the API handed back an object or a string. */
export const parseNotificationData = (data: unknown): Record<string, unknown> => {
  if (!data) return {};
  if (typeof data === 'object') return data as Record<string, unknown>;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const positiveInt = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

const nonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  // Ids are opaque uids/slugs — refuse anything that could alter the route.
  if (!v || /[/?#\s]/.test(v)) return null;
  return v;
};

/**
 * Resolve the in-app destination for a notification, or null when the
 * notification is intentionally not actionable.
 */
export function getNotificationDestination(
  notification: NotificationLike | null | undefined,
  role: NotificationRole
): string | null {
  if (!notification) return null;
  const type = notification.type || '';
  const data = parseNotificationData(notification.data);
  const bookingId = positiveInt(data.bookingId);
  const contactId = nonEmptyString(data.contactId);

  const appointment = bookingId ? `/patient/appointment/${bookingId}` : null;

  switch (type) {
    case 'booking_confirmation':
    case 'doctor_assigned':
      if (role === 'patient') return appointment || '/patient/bookings';
      if (role === 'doctor') return bookingId ? `/doctor/consultation/${bookingId}` : '/doctor/consultations';
      return null;

    case 'booking_declined':
      if (role === 'patient') return '/patient/bookings';
      if (role === 'doctor') return '/doctor/consultations';
      return null;

    case 'payment_completed':
      if (role === 'patient') return appointment || '/patient/bookings';
      if (role === 'doctor') return bookingId ? `/doctor/consultation/${bookingId}` : '/doctor/consultations';
      if (role === 'admin') return '/admin/bookings';
      return null;

    case 'message_received':
      // Provider messaging is staff-only, and only the doctor surface exists.
      return role === 'doctor'
        ? (contactId ? `/doctor/messages?contact=${contactId}` : '/doctor/messages')
        : null;

    case 'test_recommended':
    case 'results_available':
      if (role === 'patient') return '/patient/lab-results';
      if (role === 'doctor') return '/doctor/lab-tests';
      return null;

    case 'prescription_ready':
      if (role === 'patient') return '/patient/prescriptions';
      if (role === 'doctor') return bookingId ? `/doctor/consultation/${bookingId}` : '/doctor/consultations';
      return null;

    case 'nurse_assigned':
    case 'sample_collection':
      return role === 'patient' ? '/patient/bookings' : null;

    case 'consultation_reminder':
    case 'follow_up_reminder':
      return role === 'patient' ? '/patient/bookings' : null;

    // ── Referrals ──────────────────────────────────────────────────────────
    // A patient can see the referral panel on the encounter it belongs to, and
    // a RECEIVING doctor can now act on it (the received-referrals panel on the
    // referral workspace). Both destinations exist as real surfaces; nothing
    // here points at a page that does not have the thing the notification is
    // about.
    case 'referral_created':
    case 'referral_received':
      if (role === 'patient') return appointment || '/patient/bookings';
      if (role === 'doctor') return '/doctor/patient-transfer';
      return null;

    case 'referral_accepted':
    case 'referral_declined':
    case 'referral_completed':
      // The patient is the person the referral is about, so their encounter
      // page is the right destination for every referral outcome.
      return role === 'patient' ? (appointment || '/patient/bookings') : null;

    // ── Purpose-bound location ────────────────────────────────────────────
    // The patient's grant/decline and revoke controls live on the encounter.
    case 'location_request':
      return role === 'patient' ? (appointment || '/patient/bookings') : null;

    // ── Feedback request ──────────────────────────────────────────────────
    // The rating prompt lives on the completed encounter.
    case 'feedback_request':
      return role === 'patient' ? (appointment || '/patient/bookings') : null;

    default:
      // system_alert, emergency_alert, insurance_request, nurse_handoff,
      // unknown/future types: informational. No destination — these must not
      // navigate anywhere rather than navigate somewhere wrong.
      return null;
  }
}
